<template>
	<AppAutoSuggest
		:id="id"
		:options="options"
		:is-loading="isLoading"
		:name="name"
		:active-item="activeItem"
		:format-title="formatTitle"
		:placeholder="$t('task_bundle_auto_suggest_placeholder')"
		class="js-bundle-select"
		size="small"
		@input="$emit('input', $event)"
		@load="loadTaskBundleOptions($event)"
	/>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref } from 'vue';
import { type SelectItem } from '@developers/scoro-ui';
import {
	useTaskBundleAutoSuggestLazyQuery,
	TaskBundleAutoSuggestDocument,
} from '@/generated/graphql';
import { type TaskSetAutoSuggestSelectItem } from '@/features/TaskBundle';
import { useTranslation } from '@/composables';
import { type TaskSetItem } from '@/features/ProjectPhase';
import { getUniqueId } from '@/utils/misc';
import { AppAutoSuggest, AutoSuggestDefaultEmits } from '@/features/App';

export default defineComponent({
	components: {
		AppAutoSuggest,
	},
	props: {
		/**
		 * Selected dropdown item to display without fetching all list
		 */
		item: {
			type: Object as PropType<TaskSetItem>,
			required: true,
		},

		id: {
			type: String,
			required: true,
		},

		name: {
			type: String,
			required: true,
		},
	},

	emits: AutoSuggestDefaultEmits,

	setup(props) {
		const { t } = useTranslation();
		const {
			loading: isLoading,
			load,
			onResult,
		} = useTaskBundleAutoSuggestLazyQuery({
			searchKeyword: '',
		});

		const getSelectItemFromTaskSet = (
			item: TaskSetItem
		): TaskSetAutoSuggestSelectItem => {
			return {
				id: item.id,
				title: `${item.name} (${item.taskTemplatesCount} ${t(
					'task_bundle_auto_suggest_tasks'
				)})`,
				details: {
					taskTemplatesCount: item.taskTemplatesCount,
					minDays: item.minDays,
					maxDays: item.maxDays,
					title: item.name,
				},
			};
		};

		const activeItem = computed<TaskSetAutoSuggestSelectItem>(() => {
			return getSelectItemFromTaskSet(props.item);
		});

		const options = ref<SelectItem[]>([]);

		onResult((result) => {
			if (result.data?.TaskSetsList) {
				options.value =
					result.data.TaskSetsList.map((row) => {
						const item: TaskSetItem = {
							id: row.id,
							name: row.name,
							maxDays: row.maxDays,
							minDays: row.minDays,
							taskTemplatesCount: row.taskTemplatesCount,
							ordering: 0,
							uuid: getUniqueId(),
						};

						return getSelectItemFromTaskSet(item);
					}) || [];
			}
		});

		const loadTaskBundleOptions = async (searchKeyword: string) => {
			load(TaskBundleAutoSuggestDocument, { searchKeyword });
		};

		const formatTitle = (item: TaskSetAutoSuggestSelectItem) => {
			return `${item?.details?.title || ''}`;
		};

		return {
			isLoading,
			options,
			activeItem,
			loadTaskBundleOptions,
			formatTitle,
		};
	},
});
</script>
